// components/split/PreviewPane.js
// Panneau preview droit pour SplitChatView — Session 20 + Session 21

import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Code, Type, ArrowDownToLine, Maximize2, Minimize2, Save, Check, Eye, RotateCcw, Pencil, Printer } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import MarkdownMessage from '../chat/MarkdownMessage';
import { signUrl } from '../../services/signedUrlApi';

const TABS = [
  { id: 'docx', label: 'Document', color: '#C8A96E', icon: FileText },
  { id: 'html', label: 'HTML', color: '#F97316', icon: Code },
  { id: 'code', label: 'Code', color: '#3B82F6', icon: Code },
  { id: 'markdown', label: 'Markdown', color: '#A78BFA', icon: Type },
];

const PreviewPane = ({ content, onClose, isFullScreen, onToggleFullScreen, onSaveAsFile, projects = [], currentProjectId }) => {
  const [activeTab, setActiveTab] = useState(content?.type || 'docx');
  const [saveState, setSaveState] = useState('idle'); // idle | naming | saving | saved
  const [fileName, setFileName] = useState('');
  const [saveTargetId, setSaveTargetId] = useState(currentProjectId);
  // Re-sync target si on change de projet actif
  useEffect(() => { setSaveTargetId(currentProjectId); }, [currentProjectId]);
  // Mode édition client-side — pour nettoyer avant de produire un livrable (fichier projet ou téléchargement)
  // Init depuis content.editable, mais modifiable par l'user via le toggle dans le topbar Rendu/Source
  const [isEditable, setIsEditable] = useState(content?.editable === true && content?.type === 'markdown');
  const [editedContent, setEditedContent] = useState(content?.content || '');
  const [showSource, setShowSource] = useState(content?.editable === true);
  // Re-sync quand on ouvre un nouveau message (filename change suffit pour le cas ouvrir plusieurs messages)
  useEffect(() => {
    setEditedContent(content?.content || '');
    setIsEditable(content?.editable === true && content?.type === 'markdown');
    setShowSource(content?.editable === true);
    setSaveState('idle');
    // S51 — Re-sync de l'onglet actif : sinon, visualiser un DOCX/PDF puis un autre
    // (ou passer du markdown au DOCX) garde l'ancien onglet, ce qui fige la pane.
    if (content?.type) setActiveTab(content.type);
  }, [content?.content, content?.filename, content?.editable, content?.type, content?.url]);
  const isEdited = isEditable && editedContent !== (content?.content || '');
  // Contenu "actif" utilisé par Sauver / Télécharger — édité en mode édition, original sinon
  const activeContent = isEditable ? editedContent : (content?.content || '');

  // Filtrer les tabs selon le contenu
  const visibleTabs = TABS.filter(tab => {
    if (tab.id === 'code') return content?.type === 'code';
    if (tab.id === 'html') return content?.type === 'html' || content?.type === 'docx';
    if (tab.id === 'docx') return content?.type === 'docx';
    return true;
  });

  const handleDownload = async (format) => {
    if (!content?.url) return;
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const pathToSign = format === 'pdf' ? `${content.url}?format=pdf` : content.url;
      const signed = await signUrl(pathToSign);
      window.open(`${apiBase}${signed}`, '_blank');
    } catch (e) {
      console.error('Signature URL document échouée:', e);
    }
  };

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      background: 'var(--card-bg, #111520)',
      borderLeft: '1px solid var(--card-border, rgba(255,255,255,0.06))',
      minWidth: 0,
    }}>
      {/* Topbar — tout aligné sur une seule ligne, design premium compact */}
      <div style={{
        height: '42px', display: 'flex', alignItems: 'center',
        padding: '0 10px', gap: '6px',
        borderBottom: '1px solid var(--card-border, rgba(255,255,255,0.06))',
        flexShrink: 0,
      }}>
        {/* Tabs type (Document/HTML/Code/Markdown) — à gauche, seulement si >1 type visible */}
        {visibleTabs.length > 1 && (
          <div style={{ display: 'flex', gap: '2px' }}>
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                title={tab.label}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '4px 8px', borderRadius: '5px', border: 'none',
                  cursor: 'pointer', fontSize: '11px', fontWeight: 500,
                  background: activeTab === tab.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--text, #E8EDF5)' : 'var(--text-secondary, #8A9BBE)',
                  transition: 'all 0.15s ease', lineHeight: 1,
                }}
              >
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: tab.color }} />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Toggle Rendu / Source — segmented premium (markdown uniquement) */}
        {content?.type === 'markdown' && content?.content && (
          <div style={{
            display: 'flex', background: 'rgba(255,255,255,0.04)',
            borderRadius: '6px', padding: '2px', gap: '1px',
          }}>
            <button
              onClick={() => setShowSource(false)}
              title="Affichage rendu (markdown formaté)"
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 9px', borderRadius: '4px', border: 'none',
                cursor: 'pointer', fontSize: '11px', fontWeight: 600, lineHeight: 1,
                background: !showSource ? 'rgba(167,139,250,0.22)' : 'transparent',
                color: !showSource ? '#A78BFA' : 'var(--text-secondary, #8A9BBE)',
                transition: 'all 0.15s ease',
              }}>
              <Eye size={12} /> Rendu
            </button>
            <button
              onClick={() => setShowSource(true)}
              title="Affichage source (markdown brut)"
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 9px', borderRadius: '4px', border: 'none',
                cursor: 'pointer', fontSize: '11px', fontWeight: 600, lineHeight: 1,
                background: showSource ? 'rgba(255,255,255,0.1)' : 'transparent',
                color: showSource ? 'var(--text, #E8EDF5)' : 'var(--text-secondary, #8A9BBE)',
                transition: 'all 0.15s ease',
              }}>
              <Code size={12} /> Source
            </button>
          </div>
        )}

        {/* Toggle Éditer — séparé, couleur accent violet (markdown uniquement) */}
        {content?.type === 'markdown' && content?.content && (
          <button
            onClick={() => { setIsEditable(v => !v); if (!isEditable) setShowSource(true); }}
            title={isEditable ? 'Sortir du mode édition' : 'Éditer pour nettoyer / produire un livrable'}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 9px', borderRadius: '5px', lineHeight: 1,
              border: `1px solid ${isEditable ? 'rgba(167,139,250,0.6)' : 'rgba(167,139,250,0.3)'}`,
              background: isEditable ? 'rgba(167,139,250,0.2)' : 'rgba(167,139,250,0.06)',
              color: '#A78BFA',
              fontSize: '11px', fontWeight: 600, cursor: 'pointer',
              boxShadow: isEditable ? '0 0 8px rgba(167,139,250,0.3)' : 'none',
              transition: 'all 0.15s ease',
            }}>
            <Pencil size={12} /> Éditer
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* Indicateur "Édité" + réinit (combiné, visible seulement si modifié) */}
        {isEditable && isEdited && (
          <button
            onClick={() => setEditedContent(content?.content || '')}
            title="Édité — clique pour réinitialiser au contenu original"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 8px', borderRadius: '5px', lineHeight: 1,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.28)',
              color: '#ef4444', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s ease',
            }}>
            <RotateCcw size={11} /> Réinit.
          </button>
        )}

        {/* Télécharger .md (markdown uniquement) */}
        {activeContent && content?.type === 'markdown' && (
          <button
            onClick={() => {
              const blob = new Blob([activeContent], { type: 'text/markdown;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              const base = (content.filename || 'reponse-itriia').replace(/\.[^/.]+$/, '');
              a.href = url; a.download = `${base}.md`; a.click();
              URL.revokeObjectURL(url);
            }}
            title="Télécharger en .md (version éditée si mode édition)"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 9px', borderRadius: '5px', lineHeight: 1,
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.3)',
              color: '#22C55E', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s ease',
            }}>
            <ArrowDownToLine size={12} /> .md
          </button>
        )}

        {/* S51 — Export DOCX depuis markdown (preview markdown uniquement) */}
        {activeContent && content?.type === 'markdown' && (
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('oracle_token');
                const res = await fetch('/api/documents/generate-from-markdown', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    title: (content.filename || 'Document ITRIIA').replace(/\.[^/.]+$/, ''),
                    markdown: activeContent,
                    palette: 'bleu',
                    projectId: currentProjectId,
                  }),
                });
                const data = await res.json();
                if (!data.success) throw new Error(data.error || 'Erreur génération');
                const dl = await signUrl(data.download_url);
                const a = document.createElement('a');
                a.href = dl; a.download = data.filename; a.click();
              } catch (e) {
                alert('Erreur export DOCX : ' + e.message);
              }
            }}
            title="Convertir le markdown en DOCX stylé"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 9px', borderRadius: '5px', lineHeight: 1,
              background: 'rgba(29,78,216,0.12)',
              border: '1px solid rgba(29,78,216,0.35)',
              color: '#3b82f6', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s ease',
            }}>
            <ArrowDownToLine size={12} /> .docx
          </button>
        )}

        {/* Imprimer (markdown uniquement) — ouvre une fenêtre print isolée du chrome ITRIIA */}
        {activeContent && content?.type === 'markdown' && (
          <button
            onClick={() => {
              const previewEl = document.querySelector('.preview-pane-content');
              if (!previewEl) return;
              const html = previewEl.innerHTML;
              const title = (content.filename || 'Aperçu ITRIIA').replace(/\.[^/.]+$/, '');
              const printWin = window.open('', '_blank', 'width=900,height=1100');
              if (!printWin) {
                alert('Impossible d’ouvrir la fenêtre d’impression. Vérifie que les pop-ups sont autorisés pour ce site.');
                return;
              }
              printWin.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>${title.replace(/[<>"]/g, '')}</title>
<style>
  @page { size: A4; margin: 2cm; }
  * { box-sizing: border-box; }
  /* Force toutes les couleurs en noir/transparent : le DOM cloné depuis ITRIIA
     porte des couleurs claires (thème dark) qui deviennent invisibles sur fond blanc.
     On override en !important puis on rétablit ciblé pour code/pre/th/blockquote. */
  body, body * {
    color: #141414 !important;
    background-color: transparent !important;
    border-color: #ccc !important;
    text-shadow: none !important;
    box-shadow: none !important;
  }
  body { font-family: 'Garamond', 'EB Garamond', Cambria, Georgia, serif; font-size: 11pt; line-height: 1.55; margin: 0; }
  h1, h2, h3, h4 { font-family: 'Garamond', 'EB Garamond', Cambria, Georgia, serif; page-break-after: avoid; }
  h1, h2, h3, h4 { color: #1E3A2F !important; }
  h1 { font-size: 18pt; margin: 0 0 12pt; }
  h2 { font-size: 14pt; margin: 16pt 0 8pt; border-bottom: 1px solid #B8860B !important; padding-bottom: 3pt; }
  h3 { font-size: 12pt; margin: 12pt 0 6pt; }
  p { margin: 0 0 8pt; text-align: justify; }
  blockquote {
    border-left: 3px solid #B8860B !important;
    padding: 4pt 12pt;
    color: #4A4845 !important;
    background-color: #fdfaf2 !important;
    font-style: italic; margin: 8pt 0;
    page-break-inside: avoid;
  }
  code {
    font-family: 'Courier New', monospace;
    background-color: #f4f1ea !important;
    color: #1a1a1a !important;
    padding: 1pt 4pt; border-radius: 2pt; font-size: 9.5pt;
  }
  pre {
    background-color: #f4f1ea !important;
    color: #1a1a1a !important;
    padding: 8pt 10pt; border-radius: 4pt;
    overflow: auto; font-size: 9.5pt;
    page-break-inside: avoid;
  }
  pre code, pre code * {
    background-color: transparent !important;
    color: #1a1a1a !important;
    padding: 0;
  }
  table { border-collapse: collapse; width: 100%; margin: 8pt 0; page-break-inside: avoid; font-size: 10pt; }
  th, td { border: 1px solid #ccc !important; padding: 5pt 7pt; text-align: left; vertical-align: top; }
  th { background-color: #f4f1ea !important; font-weight: 600; }
  ul, ol { padding-left: 1.4em; margin: 4pt 0 8pt; }
  li { margin: 2pt 0; }
  hr { border: 0; border-top: 1px solid #ccc !important; margin: 14pt 0; }
  img { max-width: 100%; height: auto; page-break-inside: avoid; }
  a { color: #1E3A2F !important; text-decoration: underline; }
  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  /* Cache tout élément non destiné à l'impression (icônes copy, boutons, scrollbars) */
  button, .copy-btn, [aria-hidden="true"]:not(svg) { display: none !important; }
  @media print {
    html, body { background: #fff !important; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
    a[href]:after { content: ""; }
  }
</style>
</head>
<body>${html}</body>
</html>`);
              printWin.document.close();
              printWin.onload = () => {
                setTimeout(() => {
                  try { printWin.focus(); printWin.print(); } catch (e) { /* silent */ }
                }, 250);
              };
            }}
            title="Imprimer (ou enregistrer en PDF via le dialogue d'impression)"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 9px', borderRadius: '5px', lineHeight: 1,
              background: 'rgba(167,139,250,0.12)',
              border: '1px solid rgba(167,139,250,0.35)',
              color: '#A78BFA', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s ease',
            }}>
            <Printer size={12} /> Imprimer
          </button>
        )}

        {/* Sauver comme fichier projet — utilise activeContent (édité ou original).
            Target dropdown permet d'envoyer vers un AUTRE projet (extraction cross-projet) */}
        {onSaveAsFile && activeContent && (
          saveState === 'naming' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              {projects.length > 0 && (
                <select
                  value={saveTargetId || ''}
                  onChange={(e) => setSaveTargetId(e.target.value ? Number(e.target.value) : null)}
                  title="Projet cible — courant par défaut, ou envoyer vers un autre"
                  style={{
                    maxWidth: '140px', padding: '3px 5px', fontSize: '11px',
                    border: `1px solid ${String(saveTargetId) !== String(currentProjectId) ? 'rgba(167,139,250,0.5)' : 'rgba(29,78,216,0.35)'}`,
                    borderRadius: '4px', background: 'var(--input-bg, #0a0d14)',
                    color: String(saveTargetId) !== String(currentProjectId) ? '#A78BFA' : 'var(--text, #E8EDF5)',
                    outline: 'none', fontWeight: 600, lineHeight: 1,
                  }}
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {String(p.id) === String(currentProjectId) ? '● ' : ''}{p.name}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && fileName.trim()) {
                    setSaveState('saving');
                    onSaveAsFile(activeContent, fileName.trim(), saveTargetId).then(() => {
                      setSaveState('saved');
                    }).catch(() => setSaveState('idle'));
                  }
                  if (e.key === 'Escape') setSaveState('idle');
                }}
                autoFocus
                placeholder="nom-du-fichier.md"
                style={{
                  width: '140px', padding: '3px 6px', fontSize: '11px',
                  border: '1px solid rgba(29,78,216,0.4)', borderRadius: '4px',
                  background: 'var(--input-bg, #0a0d14)', color: 'var(--text, #E8EDF5)',
                  outline: 'none',
                }}
              />
              <button onClick={() => {
                if (fileName.trim()) {
                  setSaveState('saving');
                  onSaveAsFile(activeContent, fileName.trim(), saveTargetId).then(() => {
                    setSaveState('saved');
                  }).catch(() => setSaveState('idle'));
                }
              }} style={{
                background: '#1D4ED8', border: 'none', borderRadius: '4px',
                padding: '3px 8px', cursor: 'pointer', fontSize: '11px', color: 'white',
                display: 'flex', alignItems: 'center', gap: '3px',
              }}>
                <Check size={11} /> OK
              </button>
              <button onClick={() => setSaveState('idle')} style={{
                background: 'none', border: '1px solid var(--card-border)', borderRadius: '4px',
                padding: '3px 5px', cursor: 'pointer', fontSize: '11px', color: 'var(--text-secondary)',
              }}>✕</button>
            </div>
          ) : (
            <button
              onClick={() => {
                if (saveState === 'saved') return;
                const firstLine = (activeContent || '').replace(/[#*_\[\]]/g, '').trim().substring(0, 50).replace(/[^a-zA-Z0-9àâäéèêëïîôùûüçÀÂÄÉÈÊËÏÎÔÙÛÜÇ\s-]/g, '').trim();
                setFileName((firstLine.replace(/\s+/g, '-').toLowerCase().substring(0, 40) || 'reponse-itriia') + '.md');
                setSaveState('naming');
              }}
              disabled={saveState === 'saving' || saveState === 'saved'}
              title={saveState === 'saved' ? 'Sauvegardé dans le projet' : saveState === 'saving' ? 'Enregistrement…' : 'Sauvegarder comme fichier du projet'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '26px', height: '26px', padding: 0,
                background: saveState === 'saved' ? 'rgba(34,197,94,0.12)' : 'transparent',
                border: 'none', borderRadius: '6px',
                cursor: saveState === 'saved' ? 'default' : 'pointer',
                color: saveState === 'saved' ? '#22c55e' : 'var(--text-secondary)',
                opacity: saveState === 'saving' ? 0.6 : 1,
                transition: 'all 0.15s ease',
              }}
            >
              {saveState === 'saved' ? <Check size={14} /> : <Save size={14} />}
            </button>
          )
        )}

        {/* Séparateur visuel avant les actions fenêtre */}
        <div style={{ width: '1px', height: '18px', background: 'var(--card-border, rgba(255,255,255,0.08))', margin: '0 2px' }} />

        {/* Fullscreen toggle (icon-only) */}
        {onToggleFullScreen && (
          <button onClick={onToggleFullScreen} title={isFullScreen ? 'Réduire' : 'Plein écran'} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', padding: 0,
            background: isFullScreen ? 'rgba(59,130,246,0.12)' : 'transparent',
            border: 'none', cursor: 'pointer', borderRadius: '5px',
            color: isFullScreen ? '#3B82F6' : 'var(--text-secondary, #8A9BBE)',
            transition: 'all 0.15s ease',
          }}>
            {isFullScreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        )}

        {/* Close (icon-only) */}
        <button onClick={onClose} title="Fermer le panneau" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '24px', height: '24px', padding: 0,
          background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '5px',
          color: 'var(--text-secondary, #8A9BBE)',
          transition: 'all 0.15s ease',
        }}>
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <style>{`
        .preview-pane-content::-webkit-scrollbar { width: 5px; }
        .preview-pane-content::-webkit-scrollbar-track { background: transparent; }
        .preview-pane-content::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.08);
          border-radius: 4px;
        }
        .preview-pane-content::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.15);
        }
      `}</style>
      <div className="preview-pane-content" style={{ flex: 1, overflow: 'auto', padding: '16px', position: 'relative' }}>
        {!content ? (
          <EmptyState />
        ) : activeTab === 'docx' ? (
          <DocxPanel content={content} onDownload={handleDownload} />
        ) : activeTab === 'html' ? (
          <HtmlPanel content={content} />
        ) : activeTab === 'code' ? (
          <CodePanel content={content} />
        ) : (
          <MarkdownPanel
            content={content}
            editable={isEditable}
            showSource={showSource}
            editedContent={editedContent}
            onEditedContentChange={setEditedContent}
          />
        )}
      </div>
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', gap: '12px',
    color: 'var(--text-secondary, #8A9BBE)',
  }}>
    <FileText size={40} strokeWidth={1} style={{ opacity: 0.3 }} />
    <div style={{ fontSize: '13px', opacity: 0.6 }}>Aucun aperçu</div>
    <div style={{ fontSize: '11px', opacity: 0.4 }}>
      Génère un document ou ouvre un bloc de code
    </div>
  </div>
);

// ─── DOCX Panel ───────────────────────────────────────────────────────────────
const DocxPanel = ({ content, onDownload }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', gap: '20px',
  }}>
    {/* Big icon */}
    <div style={{
      width: '80px', height: '100px', borderRadius: '12px',
      background: 'linear-gradient(135deg, rgba(29,78,216,0.15), rgba(29,78,216,0.05))',
      border: '1px solid rgba(29,78,216,0.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <FileText size={36} color="#3B82F6" strokeWidth={1.5} />
    </div>

    {/* Filename */}
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text, #E8EDF5)' }}>
        {content.filename}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary, #8A9BBE)', marginTop: '4px' }}>
        Document — {content.size > 0 ? `${Math.round(content.size / 1024)} Ko` : 'Prêt'}
      </div>
    </div>

    {/* Buttons */}
    <div style={{ display: 'flex', gap: '10px' }}>
      <button onClick={() => onDownload('docx')} style={{
        padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg, #1D4ED8, #2563EB)',
        color: '#fff', fontSize: '13px', fontWeight: 600,
        boxShadow: '0 2px 8px rgba(29,78,216,0.3)',
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        <Download size={14} /> .docx
      </button>
      <button onClick={() => onDownload('pdf')} style={{
        padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg, #DC2626, #EF4444)',
        color: '#fff', fontSize: '13px', fontWeight: 600,
        boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        <Download size={14} /> .pdf
      </button>
    </div>
  </div>
);

// ─── HTML Panel ───────────────────────────────────────────────────────────────
const SCROLLBAR_CSS = `<style>
::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.25); }
html { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.12) transparent; }
</style>`;

const HtmlPanel = ({ content }) => {
  if (!content.content) return <EmptyState />;

  // Injecter le CSS de scrollbar discrète dans le HTML
  const htmlWithScrollbar = content.content.replace(
    /(<head[^>]*>)/i,
    `$1${SCROLLBAR_CSS}`
  ) || `${SCROLLBAR_CSS}${content.content}`;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '0' }}>
      {/* Chrome bar + download */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '8px 12px',
        background: 'rgba(255,255,255,0.03)', borderRadius: '8px 8px 0 0',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#FF5F56' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#FFBD2E' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#27C93F' }} />
        </div>
        {content.pdfUrl && (
          <button
            onClick={() => window.open(content.pdfUrl, '_blank', 'noopener,noreferrer')}
            title="Télécharger la version PDF premium"
            style={{
              background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.35)',
              borderRadius: '4px', padding: '3px 10px', cursor: 'pointer',
              color: '#EF4444', fontSize: '11px', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: '5px', marginRight: '6px',
            }}
          >
            <ArrowDownToLine size={12} /> .pdf
          </button>
        )}
        <button
          onClick={() => {
            const blob = new Blob([content.content], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = content.filename || 'page.html'; a.click();
            URL.revokeObjectURL(url);
          }}
          title="Télécharger HTML"
          style={{
            background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: '4px', padding: '3px 10px', cursor: 'pointer',
            color: '#22C55E', fontSize: '11px', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '5px',
          }}
        >
          <ArrowDownToLine size={12} /> .html
        </button>
      </div>
      <iframe
        srcDoc={htmlWithScrollbar}
        sandbox="allow-scripts"
        title="HTML Preview"
        style={{
          flex: 1, border: '1px solid var(--card-border)',
          borderRadius: '0 0 8px 8px', background: '#fff',
          borderTop: 'none',
        }}
      />
    </div>
  );
};

// ─── Code Panel ───────────────────────────────────────────────────────────────
const CodePanel = ({ content }) => {
  if (!content.content) return <EmptyState />;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header avec langue + download */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '8px 12px',
        background: 'rgba(255,255,255,0.03)', borderRadius: '8px 8px 0 0',
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: '11px', fontWeight: 600, color: '#3B82F6',
          background: 'rgba(59,130,246,0.1)', padding: '2px 8px',
          borderRadius: '4px', flex: 0,
        }}>
          {content.language || 'text'}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={() => {
            const blob = new Blob([content.content], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = content.filename || 'code.txt'; a.click();
            URL.revokeObjectURL(url);
          }}
          style={{
            background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '4px', padding: '3px 10px', cursor: 'pointer',
            color: '#3B82F6', fontSize: '11px', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '5px',
          }}
        >
          <ArrowDownToLine size={12} /> Télécharger
        </button>
      </div>
      {/* Syntax highlighted code */}
      <div style={{ flex: 1, overflow: 'auto', borderRadius: '0 0 8px 8px' }}>
        <SyntaxHighlighter
          style={oneDark}
          language={content.language || 'text'}
          PreTag="pre"
          showLineNumbers
          customStyle={{
            margin: 0,
            padding: '16px',
            fontSize: '13px',
            lineHeight: 1.6,
            minHeight: '100%',
            borderRadius: '0 0 8px 8px',
          }}
        >
          {content.content}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

// ─── Markdown Panel ───────────────────────────────────────────────────────────
// Session 35 : rend le markdown via MarkdownMessage (ReactMarkdown + remark-gfm + Prism).
// Session 44 : toggles Rendu/Source/Éditer remontés dans le topbar PreviewPane
// (ce panneau ne gère plus que l'affichage du contenu lui-même).
const MarkdownPanel = ({ content, editable = false, showSource = false, editedContent, onEditedContentChange }) => {
  if (!content.content) return <EmptyState />;

  // Contenu actuellement affiché — édité si mode édition, original sinon
  const displayedContent = editable ? (editedContent ?? '') : content.content;

  return (
    <div style={{
      height: '100%', overflow: 'auto',
      padding: editable && showSource ? 0 : '20px 22px',
      background: 'rgba(255,255,255,0.02)',
      borderRadius: 10, border: `1px solid ${editable && showSource ? 'rgba(167,139,250,0.35)' : 'var(--card-border)'}`,
      boxShadow: editable && showSource ? '0 0 12px rgba(167,139,250,0.15) inset' : 'none',
    }}>
      {showSource ? (
        editable ? (
          <textarea
            value={displayedContent}
            onChange={(e) => onEditedContentChange?.(e.target.value)}
            spellCheck={false}
            placeholder="Édite librement… Les actions Sauver / Télécharger utilisent cette version."
            style={{
              width: '100%', height: '100%', minHeight: '300px',
              padding: '20px 22px', margin: 0,
              background: 'transparent', border: 'none', outline: 'none', resize: 'none',
              color: 'var(--text, #E8EDF5)',
              fontSize: 13, lineHeight: 1.65,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <pre style={{
            margin: 0, fontSize: 12, lineHeight: 1.6,
            color: 'var(--text-secondary, #8A9BBE)',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}>{displayedContent}</pre>
        )
      ) : (
        <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text, #E8EDF5)' }}>
          <MarkdownMessage content={displayedContent} />
        </div>
      )}
    </div>
  );
};

export default PreviewPane;
